import * as yup from "yup";
import { addNotification } from "portal/state/notifications";
import { atkEquipmentApi } from "common/state/portalApi/atkEquipment";
import { Autotractor } from "protos/atk/autotractor";
import { buildPermission } from "portal/utils/auth";
import { convert } from "portal/utils/units/units";
import { Formik, useFormikContext } from "formik";
import { getRigDetailsPath } from "portal/utils/routing";
import {
  loadFormStateFromRig,
  RigDetailsComputedFormState,
  RigDetailsFormState,
  saveFormStateToRig,
} from "./rig-details/FormState";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { portalApi } from "portal/state/portalApi";
import {
  resolveImplementConfiguration,
  resolveTractorConfiguration,
} from "portal/utils/atkEquipment";
import { RigConnections } from "./rig-details/RigConnections";
import { RigPartsAndConfiguration } from "./rig-details/RigPartsAndConfiguration";
import { RigValidationContext } from "./RigValidationContext";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { UnauthorizedPage } from "portal/components/UnauthorizedPage";
import { useDispatch } from "react-redux";
import { useJigsawMetrics } from "../../utils/hooks/useJigsawMetrics";
import { useLocation, useNavigate, useParams } from "react-router";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import { withAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import React, { useEffect, useMemo, useState } from "react";

// checklists are free-form, so the ground prep one is matched by name
const GROUND_PREP_CHECKLIST_PATTERN = /ground[\s-]*prep/i;

export type RigDetailsPageView =
  | "status-and-config"
  | "connections"
  | "tractor-library"
  | "implement-library";
export type RigDetailsSwitchViewCallback = (view: RigDetailsPageView) => void;

interface RigDetailsPageProps {
  isNewRig: boolean;
}

// use location.state for the "copy as new" functionality
export interface RigDetailsPageLocationState {
  initialRig: Autotractor | undefined;
}

const RigDetailsComputedFormStateProvider: React.FC<
  React.PropsWithChildren
> = ({ children }) => {
  const { values } = useFormikContext<RigDetailsFormState>();
  const { t } = useTranslation();

  const computedState = useMemo<RigDetailsComputedFormState>(() => {
    const rig = saveFormStateToRig(values);
    const resolvedTractorConfig = values.tractor
      ? resolveTractorConfiguration(t, values.tractor)
      : undefined;
    const resolvedImplementConfigs = values.implements.map((implement) =>
      resolveImplementConfiguration(t, implement)
    );

    const scvOutputsById = Object.fromEntries(
      (resolvedTractorConfig?.scvOutputs?.scvOutput ?? []).map((output) => [
        output.id,
        output,
      ])
    );

    const scvInputsById = Object.fromEntries(
      resolvedImplementConfigs.flatMap((implement) =>
        (implement.scvInputs?.inputs ?? []).map((input) => [input.id, input])
      )
    );

    const scvConnectionSelectOptions = Object.values(
      values.scvConnectionsByInput
    ).flatMap((connection) => {
      for (const implement of resolvedImplementConfigs) {
        for (const input of implement.scvInputs?.inputs ?? []) {
          // using SCV connections for certain applications requires position
          // feedback from an attached IO-Link sensor.  figure out which of
          // those we have.
          let positionFeedback = false;

          if (values.atkConfig.ioLink.present) {
            for (const reg of values.atkConfig.ioLink.config.readRegisters) {
              if (reg.scvPosition.scvConnectionId === connection.id) {
                positionFeedback = true;
              }
            }
          }

          if (input.id === connection.implementScvInputId) {
            return {
              connectionId: connection.id,
              implement: implement.raw,
              scvInput: input,
              positionFeedback,
            };
          }
        }
      }

      return [];
    });

    return {
      rig,
      resolvedTractorConfig,
      resolvedImplementConfigs,
      scvOutputsById,
      scvInputsById,
      scvConnectionSelectOptions,
    };
  }, [t, values]);

  // debounce the computed value of the rig; invalidate the validation result
  // when the rig changes, but only issue the remote validation request once it
  // settles.
  const [triggerPrevalidate, prevalidateResult] =
    atkEquipmentApi.endpoints.prevalidateRig.useLazyQuery();
  const [isPendingSettle, setPendingSettle] = useState(false);

  useEffect(() => {
    setPendingSettle(true);
    const timeout = setTimeout(() => {
      triggerPrevalidate({ rig: computedState.rig });
      setPendingSettle(false);
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [computedState.rig, triggerPrevalidate]);

  return (
    <RigDetailsComputedFormState.Provider value={{ ...computedState }}>
      <RigValidationContext.Provider
        value={{
          rig: computedState.rig,
          validationResult: prevalidateResult.data,
          validationResultLoading:
            isPendingSettle || prevalidateResult.isFetching,
        }}
      >
        {children}
      </RigValidationContext.Provider>
    </RigDetailsComputedFormState.Provider>
  );
};

const _RigDetailsPage: React.FC<RigDetailsPageProps> = ({ isNewRig }) => {
  const { rigId, serial } = useParams();
  const { state: locationState } = useLocation();
  const { initialRig: locationStateInitialRig } = locationState
    ? (locationState as RigDetailsPageLocationState)
    : { initialRig: undefined };
  const [view, setView] = useState<RigDetailsPageView>("status-and-config");
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const useJigsaw = useJigsawMetrics();

  const { data: searchResult } = useQueryPopups(
    atkEquipmentApi.endpoints.searchRigs.useQuery({ rigId }, { skip: isNewRig })
  );

  const [createRig] = useMutationPopups(
    atkEquipmentApi.endpoints.createRig.useMutation({})
  );
  const [updateRig] = useMutationPopups(
    atkEquipmentApi.endpoints.updateRig.useMutation({})
  );

  async function onSave(values: RigDetailsFormState): Promise<void> {
    const rigToSave = saveFormStateToRig(values);
    let result: { ok: false } | { ok: true; name: string; id: string } = {
      ok: false,
    };

    if (isNewRig) {
      const response = await createRig(rigToSave);

      if (!response.error) {
        result = {
          ok: true,
          name: response.data.customerProvidedName,
          id: response.data.id,
        };
      }
    } else {
      const response = await updateRig(rigToSave);

      if (!response.error) {
        result = {
          ok: true,
          name: response.data.customerProvidedName,
          id: response.data.id,
        };
      }
    }

    if (result.ok) {
      dispatch(
        addNotification({
          message: t("utils.actions.savedLong", {
            subject: `"${result.name}"`,
          }),
          variant: "success",
        })
      );
      navigate(getRigDetailsPath(result.id));
    }
  }

  const initialRig = searchResult?.rigs[0] ?? locationStateInitialRig;
  const effectiveSerial = initialRig?.serial ?? serial;

  // rigs with no safety checklist set default to a ground prep checklist
  const { data: checklistDefinitions } = useQueryPopups(
    rtcJobsApi.endpoints.listStartupChecklistDefinitions.useQuery()
  );
  const defaultChecklistDefinitionId = useMemo(
    () =>
      checklistDefinitions?.checklistDefinitions.find((checklist) =>
        GROUND_PREP_CHECKLIST_PATTERN.test(checklist.content?.name ?? "")
      )?.id ?? 0,
    [checklistDefinitions]
  );

  // ### ROBOT
  const { data: summary } = useQueryPopups(
    portalApi.endpoints.getRobot.useQuery(
      effectiveSerial
        ? { language: i18n.language, serial: effectiveSerial, useJigsaw }
        : skipToken,
      {
        pollingInterval: convert(1).from("min").to("ms"),
      }
    )
  );

  let body: JSX.Element;

  if (view === "connections") {
    body = <RigConnections switchToView={setView} summary={summary} />;
  } else {
    body = (
      <RigPartsAndConfiguration
        isNewRig={isNewRig}
        view={view}
        switchToView={setView}
        serial={effectiveSerial}
        summary={summary}
      />
    );
  }

  return (
    <Formik
      initialValues={loadFormStateFromRig(
        initialRig,
        initialRig ? initialRig.serial : serial,
        initialRig
          ? initialRig.customerId
          : summary?.customer?.db?.id.toString(),
        initialRig ? initialRig.customerUuid : summary?.customer?.uuid,
        defaultChecklistDefinitionId
      )}
      validationSchema={yup.object({
        name: yup.string().required(t("utils.form.required")),
      })}
      enableReinitialize={true}
      onSubmit={onSave}
    >
      <RigDetailsComputedFormStateProvider>
        {body}
      </RigDetailsComputedFormStateProvider>
    </Formik>
  );
};

export const RigDetailsPage = withAuthorizationRequired(
  {
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.atk_equipment,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.atk_equipment,
        PermissionDomain.all
      ),
    ],
  },
  _RigDetailsPage,
  <UnauthorizedPage />
);
